import React, { useState, useMemo } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "../services/apiService";
import { PHARMACY_HALO_COLORS } from "../config/colors";
import { API_PATHS } from "../config/apiConstants";
import PharmacyDataGrid from "../components/PharmacyDataGrid";

const Subscriptions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    const fetchSubs = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await phappsRequest(API_PATHS.CHEMIST_SUBS, "GET");
        if (response && Array.isArray(response)) {
          setData(response);
        } else if (response && response.ChemistSubs) {
          setData(response.ChemistSubs);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error("Error fetching ChemistSubs:", err);
        setError("Error fetching subscription data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubs();
  }, []);

  // Transform data to match PharmacyDataGrid expected structure or pass as is if compatible
  // PharmacyDataGrid expects a specific structure in `data.MainData.pharmacyData`
  // We need to adapt the subscription data to fit or update PharmacyDataGrid to be more flexible.
  // Since the user asked to use PharmacyDataGrid, and that component seems tailored to the main dashboard,
  // we should probably pass the data in a format it accepts, OR better yet, 
  // modify PharmacyDataGrid to accept custom columns/rows if needed, but looking at PharmacyDataGrid,
  // it has hardcoded columns. 
  // 
  // HOWEVER, the user explicitly asked for: "it will just have the pharmacy name, QudaSubStatus,DocLinkSubStatus and an open button."
  // PharmacyDataGrid has HARDCODED columns for stats. 
  // To use PharmacyDataGrid effectively here without breaking existing dashboards, we should likely
  // refactor PharmacyDataGrid to accept `columns` and `rows` props, or a `mode` prop.
  // 
  // Let's check PharmacyDataGrid.jsx content again. It takes `data` prop and extracts `data.MainData.pharmacyData`.
  // It also has hardcoded columns.
  // 
  // To fulfill the user request "use the datagrid import PharmacyDataGrid", I should probably
  // make PharmacyDataGrid more reusable by allowing it to accept custom columns/rows override.
  
  const subsData = useMemo(() => ({
    MainData: {
      pharmacyData: data.map(item => ({
        chemistID: item.PHUUID || item.ChemistID,
        pharmacyName: item.ChemistName,
        // Add dummy values for other fields required by formatPharmacyRow in PharmacyDataGrid
        // or meaningful ones if available.
        qudaStats: {},
        waitingListStats: {},
        doclinkStats: {},
        // Custom fields for subscriptions
        QudaSubStatus: item.QudaSubStatus,
        DocLinkSubStatus: item.DocLinkSubStatus
      }))
    }
  }), [data]);

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Box className="text-center">
          <CircularProgress
            size={60}
            sx={{ color: PHARMACY_HALO_COLORS.BLUE, mb: 2 }}
          />
          <Typography variant="h6" color="textSecondary">
            Loading subscriptions...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Alert
          severity="error"
          sx={{
            maxWidth: 600,
            "& .MuiAlert-icon": { color: PHARMACY_HALO_COLORS.RED },
          }}
        >
          <Typography variant="h6" className="mb-2">
            {error}
          </Typography>
          <Typography variant="body2">
            Please check your connection and try again.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="space-y-4">
      <Typography
        variant="h4"
        className="font-bold mb-6"
        style={{ color: PHARMACY_HALO_COLORS.BLUE }}
      >
        Subscriptions
      </Typography>
      
      {/* 
        We need to pass a custom prop to PharmacyDataGrid to tell it to use different columns 
        and skip the stats summary if possible. 
        Currently PharmacyDataGrid is very specific. 
        I will modify PharmacyDataGrid to accept `customColumns` and `customRowTransformer`.
      */}
      <PharmacyDataGrid 
        data={subsData} 
        isSubscriptionMode={true}
      />
    </Box>
  );
};

export default Subscriptions;
